import { formatCurrency } from '../format/currency.js';
import { buildCsvLines, type CsvValue } from '../format/csv.js';
import { formatDate, monthBounds, previousMonthBounds } from '../format/date.js';
import { formatHours, formatInteger, formatNumber, formatPercent } from '../format/number.js';
import type { BuiltInRole } from '../types/enums.js';

/**
 * Rapporterne paa /rapporter - formen de har, og hvordan de skrives.
 *
 * **Én form til alle tre udgaver.** En rapport bygges én gang som et `ReportDocument` - noegletal,
 * tabeller og forbehold - og siden, CSV-filen og PDF'en tegnes alle af det. Tre kopier af det
 * samme regnestykke ville kunne naa hver sit tal, og den forkerte ville vaere den, der blev
 * sendt videre.
 *
 * **Typen staar paa kolonnen, ikke i vaerdien.** En vaerdi er et tal eller en tekst, og kolonnen
 * siger, om tallet er kroner, timer eller procent. Det er det, der goer formateringen til én
 * funktion her - `formatReportValue` - frem for en i hver udgave. Jf. CLAUDE.md: formatering
 * ligger i packages/shared og ingen andre steder.
 */

/** Noeglen i API'et: `GET /reports/:key`. Engelsk, som resten af API'et. */
export const REPORT_KEYS = [
  'monthly',
  'billing-by-month',
  'cashflow',
  'wip',
  'change-requests',
  'contract-usage',
  'customer-projects',
  'service-agreements',
  'company-checks',
  'capacity',
  'completion',
  'burndown',
  'pipeline',
  'alarms',
  'activity',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

/**
 * `decimal` er et tal med to decimaler uden enhed - fx 1,71 projekter pr. kunde. Ikke `hours`,
 * selvom de formateres ens: typen siger, hvad tallet er, og et gennemsnit af projekter er ikke timer.
 */
export type ReportValueType =
  'text' | 'money' | 'hours' | 'percent' | 'integer' | 'decimal' | 'date' | 'days';
export type ReportTone = 'neutral' | 'success' | 'warning' | 'danger';
export type ReportValue = string | number | null;
export type ReportRow = Record<string, ReportValue>;

export interface ReportColumn {
  key: string;
  /** Dansk. Kolonneoverskriften i alle tre udgaver. */
  label: string;
  type: ReportValueType;
}

export interface ReportTable {
  id: string;
  /** Dansk. Afsnittets overskrift. */
  title: string;
  /** Dansk. Hvad afsnittet viser, og hvad der ikke er med. */
  description?: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  /** Sumlinjen. Kun de kolonner, der giver mening at laegge sammen - resten staar tomme. */
  total?: ReportRow;
  /** Dansk. Det, afsnittet siger, naar der ingen raekker er. */
  empty: string;
  /**
   * A tone per cell, by row index and column key - the capacity calendar colours its weeks this
   * way. The page and the PDF tint the cell; the CSV has the value alone. A missing entry has no
   * tone.
   */
  cell_tones?: Array<Partial<Record<string, ReportTone>>>;
  /** What the tones mean, under the table on the page and in the PDF. Danish. */
  legend?: Array<{ tone: ReportTone; label: string }>;
  /**
   * Rows per page on the page, for a table that can run long - the pipeline report's boards. The
   * CSV and the PDF have every row, and `total` is always the whole table's. Unset: one page.
   */
  page_size?: number;
  /**
   * Links on cells, by row index and column key - a board to its contract. Only the page uses
   * them, and only for a user who may open what they point to; the CSV and the PDF have the text.
   */
  links?: Array<Partial<Record<string, ReportLink>>>;
  /**
   * What lies behind a number, by row index: the page makes `column`'s cell a button that opens
   * the rows that add up to it - the tasks behind a board's count. Not in the CSV or the PDF.
   */
  details?: { column: string; rows: Array<ReportDetail | null> };
}

/** Where a cell links to. The page builds the path and checks the user may go there. */
export type ReportLink = { kind: 'contract'; contract_no: string };

/** The rows behind one cell - see `ReportTable.details`. */
export interface ReportDetail {
  /** Danish. Above the rows when they are open. */
  title: string;
  columns: ReportColumn[];
  rows: ReportRow[];
}

export interface ReportKpi {
  label: string;
  value: ReportValue;
  type: ReportValueType;
  hint?: string;
  tone?: ReportTone;
}

/**
 * What a report is for when it is not a period - the burndown's project. The server lists the
 * options and says which one the report is for; the page shows them as a picker and sends the
 * chosen value back as `?<param>=`. The CSV and the PDF are for the one chosen.
 */
export interface ReportChoice {
  /** The query parameter, e.g. `project`. */
  param: string;
  /** Danish. The picker's label. */
  label: string;
  /** The value the report is for. Null when there is nothing to choose from. */
  selected: string | null;
  options: Array<{ value: string; label: string }>;
}

export interface ReportDocument {
  key: ReportKey;
  title: string;
  /** Perioden eller opgoerelsestidspunktet, som det staar under titlen. Dansk. */
  period_label: string;
  /** Maaneden, en maanedsrapport er for - `YYYY-MM`. Null paa en opgoerelse "som nu". */
  month: string | null;
  /** The calendar year a yearly report is for. Absent on the others. */
  year?: number;
  /** The project or other choice the report is for - see `ReportChoice`. Absent on the others. */
  choice?: ReportChoice;
  generated_at: string;
  currency: string;
  kpis: ReportKpi[];
  sections: ReportTable[];
  /**
   * Forbehold, der skal staa ved tallene - fx at alle fakturaer i BC endnu er kladder, eller at
   * en aftale uden sats ikke har et beloeb. Dansk. Staar i alle tre udgaver: et tal, der rejser
   * uden sit forbehold, bliver laest som noget andet, end det er.
   */
  notes: string[];
}

/* ------------------------------------------------------------------------------------------ */
/* Kapacitetskalenderen                                                                        */
/* ------------------------------------------------------------------------------------------ */

/**
 * The calendar on /rapporter/kapacitet: the capacity report's weeks, but as far ahead as one
 * scrolls - in weeks, or zoomed out to months. `GET /reports/capacity/calendar?unit=&count=`.
 * The report's own table, CSV and PDF stay at `CAPACITY_WEEKS`.
 */
export const CAPACITY_UNITS = ['week', 'month'] as const;
export type CapacityUnit = (typeof CAPACITY_UNITS)[number];

/** How many periods the calendar reads, and the most it can be asked for. */
export const CAPACITY_CALENDAR_DEFAULT: Record<CapacityUnit, number> = { week: 52, month: 12 };
export const CAPACITY_CALENDAR_MAX: Record<CapacityUnit, number> = { week: 104, month: 24 };

/** A task's hours in one period of the calendar - what a cell is made of. */
export interface CapacityTask {
  name: string;
  board: string | null;
  /** The task's hours that fall in the period, as planned. */
  hours: number;
  deadline: string | null;
}

/** One person in one period. Hours. */
export interface CapacityCell {
  /** The weekly norm x the person's utilization target, less absence and days off. */
  available: number;
  /** Planned in the period. */
  booked: number;
  /** What the period before could not take, booked here on top. */
  carried_in: number;
  free: number;
  tone: ReportTone;
  /** The tasks behind `booked`, most hours first. */
  tasks: CapacityTask[];
}

export interface CapacityCalendar {
  unit: CapacityUnit;
  generated_at: string;
  /** Danish date. The first period counts from it. */
  today: string;
  /** First and last day; `week` is the ISO week number, null for a month. */
  periods: Array<{ start: string; end: string; week: number | null }>;
  /** `ready` is the index of the person's first free period, or null. */
  people: Array<{ id: string; name: string; ready: number | null; cells: CapacityCell[] }>;
  /**
   * Unassigned hours as planned, what the team's free hours take of them, and the tasks behind
   * `planned`, per period.
   */
  unassigned: Array<{ planned: number; taken: number; tasks: CapacityTask[] }>;
  /** The team after the unassigned work. `success` = room for a new project. */
  team: Array<{ available: number; free: number; tone: ReportTone }>;
  /** The first period with room for a new project, or null. */
  team_ready: number | null;
  /** What room for a new project is: one usable week - the weekly norm x the target - free. */
  project_hours: number;
  /** Sources that did not answer, in Danish. Empty when all did. */
  warnings: string[];
}

/** En rapport som fil - CSV eller PDF. Samme form som fakturaens PDF, se live-invoices.ts. */
export interface ReportExport {
  file_name: string;
  content_type: string;
  content_base64: string;
}

export type ReportExportFormat = 'csv' | 'pdf';

/* ------------------------------------------------------------------------------------------ */
/* Kataloget                                                                                   */
/* ------------------------------------------------------------------------------------------ */

export type ReportGroup = 'finance' | 'operations';

export const REPORT_GROUP_LABELS: Record<ReportGroup, string> = {
  finance: 'Økonomi',
  operations: 'Drift',
};

export interface ReportDefinition {
  key: ReportKey;
  /** Adressen i UI'et: `/rapporter/:path`. Dansk, som de oevrige sider. */
  path: string;
  title: string;
  description: string;
  group: ReportGroup;
  /**
   * `month`: rapporten er for en maaned, og siden har en maanedsvaelger. `now`: en opgoerelse
   * af, hvordan det staar lige nu - en fremskrivning eller en beholdning har ingen periode.
   * `year`: a calendar year, with a year picker - `?year=YYYY`, the current year without it.
   */
  period: 'month' | 'now' | 'year';
  /**
   * Maaneden, en maanedsrapport aabner paa: `previous` er sidste afsluttede maaned, `current`
   * den igangvaerende. Maanedsrapporten opsummerer en lukket maaned; aktiviteten og alarmerne
   * spoerges der til, mens maaneden staar paa - og i starten af en maaned ville "sidste maaned"
   * vise noget andet, end man lige har lavet. Kun paa `period: 'month'`.
   */
  defaultMonth?: 'previous' | 'current';
  /**
   * Rollerne, der kan se den, foer nogen har rettet i dem. Hvem der faktisk kan, staar i
   * `codepass_role_reports` og rettes paa /admin/roller - se `canSeeReport`. Aktivitetsrapporten
   * er om kollegerne og er derfor fra start kun for admin.
   */
  defaultRoles: readonly BuiltInRole[];
}

/** Penge ind og ud: fakturering, likviditet, WIP, intern tid og alarmernes taerskler. */
const FINANCE: readonly BuiltInRole[] = ['admin', 'finance'];

/**
 * Det, en saelger bruger foer og efter et salg: tillaeg, kundens moenster, betalingsevne,
 * aftalernes forbrug og hvornaar der er plads til et nyt projekt.
 */
const SALES: readonly BuiltInRole[] = ['admin', 'finance', 'sales'];

export const REPORTS: readonly ReportDefinition[] = [
  {
    key: 'monthly',
    path: 'maanedlig',
    title: 'Månedsrapport',
    description:
      'Faktureret pr. kunde, nye fakturakladder, ufaktureret arbejde pr. aftale, åbne alarmer ' +
      'ved månedens slutning og top 5 kunder og aftaler.',
    group: 'finance',
    period: 'month',
    defaultMonth: 'previous',
    defaultRoles: FINANCE,
  },
  {
    key: 'billing-by-month',
    path: 'fakturering-pr-maaned',
    title: 'Fakturering pr. måned',
    description:
      'Årets måneder, januar til december: faktureret mod budgettet i Business Central, ' +
      'akkumuleret, kladder og samme måned sidste år.',
    group: 'finance',
    period: 'year',
    defaultRoles: FINANCE,
  },
  {
    key: 'cashflow',
    path: 'likviditet',
    title: 'Likviditetsprognose',
    description:
      'Forventede indbetalinger de næste 13 uger ud fra fakturaernes forfaldsdatoer. Bogførte ' +
      'og kladder hver for sig.',
    group: 'finance',
    period: 'now',
    defaultRoles: FINANCE,
  },
  {
    key: 'wip',
    path: 'wip',
    title: 'WIP-analyse',
    description:
      'Ufaktureret arbejde pr. kunde og aftale, delt på alder, og hvad det er værd til ' +
      'aftalens timepris.',
    group: 'finance',
    period: 'now',
    defaultRoles: FINANCE,
  },
  {
    key: 'change-requests',
    path: 'tillaeg',
    title: 'Tillæg og ændringer',
    description:
      'Ændringsanmodninger som andel af den oprindelige aftale, pr. projekt og samlet — ' +
      'det, man kan forvente som tillæg på kommende projekter.',
    group: 'finance',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'customer-projects',
    path: 'projekter-pr-kunde',
    title: 'Projekter pr. kunde',
    description:
      'Hvor mange projekter en kunde har i snit, hvor ofte ét projekt bliver til flere, og hvor ' +
      'længe der går til det næste — det, man kan forvente af en ny kunde.',
    group: 'finance',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'company-checks',
    path: 'virksomhedstjek',
    title: 'Virksomhedstjek',
    description:
      'Kundernes økonomi og betalingsevne: vurdering pr. kunde, røde flag og gennemsnitlige ' +
      'nøgletal fra deres årsrapporter og betalingerne i BC.',
    group: 'finance',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'contract-usage',
    path: 'aftaleforbrug',
    title: 'Aftaleforbrug',
    description:
      'Pr. aftale: solgte, brugte og fakturerede timer, kontraktsum mod faktureret, buffer og ' +
      'den reelle timepris.',
    group: 'operations',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'service-agreements',
    path: 'serviceaftaler',
    title: 'Serviceaftaler',
    description:
      'Antallet af serviceaftaler, og pr. aftale hvor mange timer en opgave bruger i snit ' +
      'holdt op mod timer pr. opgave.',
    group: 'operations',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'capacity',
    path: 'kapacitet',
    title: 'Kapacitet',
    description:
      'En kalender over de næste 12 uger: ledige timer pr. medarbejder og uge — normtiden gange ' +
      'målet for faktureringsgrad, efter fravær, fridage og planlagte opgaver i OnePro — og ' +
      'hvornår der er plads til et nyt projekt.',
    group: 'operations',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'completion',
    path: 'forventet-afslutning',
    title: 'Forventet afslutning',
    description:
      'Pr. igangværende projekt i OnePro: timer tilbage, tempoet de seneste uger, hvornår det ' +
      'forventes færdigt, og om det holder tavlens deadline.',
    group: 'operations',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'burndown',
    path: 'burndown',
    title: 'Burndown',
    description:
      'Ét projekt ad gangen: timerne tilbage uge for uge, det samlede estimat, og hvor tempoet ' +
      'de seneste uger fører hen mod tavlens deadline.',
    group: 'operations',
    period: 'now',
    defaultRoles: SALES,
  },
  {
    key: 'pipeline',
    path: 'pipeline',
    title: 'Pipeline og intern tid',
    description:
      'De ventende timer i OnePro delt på internt og eksternt arbejde, hvornår de forfalder, og ' +
      'hvor meget internt fylder af årets registrerede tid, måned for måned, pr. tavle og pr. person.',
    group: 'operations',
    period: 'year',
    defaultRoles: FINANCE,
  },
  {
    key: 'alarms',
    path: 'alarmstatistik',
    title: 'Alarmstatistik',
    description:
      'Alarmer pr. regel og pr. modtager: rejst, lukket og afvist, og hvor længe de var ' +
      'åbne. Til at justere reglernes tærskler.',
    group: 'operations',
    period: 'month',
    defaultMonth: 'current',
    defaultRoles: FINANCE,
  },
  {
    key: 'activity',
    path: 'aktivitet',
    title: 'Aktivitetsrapport',
    description:
      'Hvad hver bruger har gjort i Codepass i måneden: fakturaer, alarmer og logins. Bygget ' +
      'af audit-loggen.',
    group: 'operations',
    period: 'month',
    defaultMonth: 'current',
    defaultRoles: ['admin'],
  },
];

export function reportDefinition(key: string): ReportDefinition | undefined {
  return REPORTS.find((report) => report.key === key);
}

export function reportDefinitionByPath(path: string): ReportDefinition | undefined {
  return REPORTS.find((report) => report.path === path);
}

/** The years a yearly report can be asked for. `?year=` outside them gives the current year. */
export const REPORT_YEAR_MIN = 2020;
export const REPORT_YEAR_MAX = 2100;

/** `YYYY` within the bounds above. Det, `?year=` skal vaere. */
export function isReportYear(value: string | null | undefined): value is string {
  if (typeof value !== 'string' || !/^\d{4}$/.test(value)) return false;
  const year = Number(value);
  return year >= REPORT_YEAR_MIN && year <= REPORT_YEAR_MAX;
}

/** The year a yearly report opens on: the current one. Here, so the page and the API agree. */
export function defaultReportYear(now: Date): number {
  return now.getFullYear();
}

/** `YYYY-MM` med en gyldig maaned. Det, `?month=` skal vaere. */
export function isReportMonth(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

/**
 * Maaneden rapporten aabner paa, som `YYYY-MM` - se `defaultMonth`.
 *
 * Her og ikke paa siden, saa siden og API'et er enige: en rapport hentet uden `?month=` er den
 * samme, som siden viser, naar den aabnes.
 */
export function defaultReportMonth(definition: ReportDefinition, now: Date): string {
  const bounds =
    definition.defaultMonth === 'current' ? monthBounds(now) : previousMonthBounds(now);
  return bounds.start.slice(0, 7);
}

/* ------------------------------------------------------------------------------------------ */
/* Formateringen                                                                               */
/* ------------------------------------------------------------------------------------------ */

/**
 * Vaerdien som den vises - paa siden og i PDF'en. Null er em-streg: "ikke kendt" og ikke nul.
 *
 * Procent med én decimal, naar tallet ikke er helt: en tillaegsandel paa 12,5 % er en anden
 * oplysning end 13 %, og det er netop den slags tal, rapporterne bruges til at sammenligne.
 */
export function formatReportValue(
  type: ReportValueType,
  value: ReportValue,
  currency = 'DKK',
): string {
  if (value === null || value === '') return '—';
  if (type === 'text') return String(value);

  if (type === 'date') return formatDate(String(value));

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  switch (type) {
    case 'money':
      return formatCurrency(number, currency);
    case 'hours':
      return formatHours(number);
    case 'percent':
      return formatPercent(number, Number.isInteger(number) ? 0 : 1);
    case 'integer':
      return formatInteger(number);
    case 'decimal':
      return formatNumber(number, 2);
    case 'days':
      return `${formatInteger(number)} ${number === 1 ? 'dag' : 'dage'}`;
  }
}

/**
 * Vaerdien i CSV-filen: et tal Excel kan regne med.
 *
 * Uden tusindtalsseparator og uden `kr` - "1245,50" og ikke "1.245,50 kr". Den formaterede udgave
 * ville dansk Excel laese som tekst, og saa kunne ingen summere kolonnen. Decimalkomma, fordi det
 * er det, en dansk maskine laeser som et tal. Tallene er skrevet af Codepass selv og gaar derfor
 * uden om formelvaernet - se `CsvRaw`.
 */
export function csvReportValue(type: ReportValueType, value: ReportValue): CsvValue {
  if (value === null || value === '') return null;
  if (type === 'text') return String(value);
  if (type === 'date') return formatDate(String(value));

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  const decimals = type === 'integer' || type === 'days' ? 0 : type === 'percent' ? 1 : 2;
  return { raw: formatNumber(number, decimals).replaceAll('.', '') };
}

/**
 * Hele rapporten som CSV: noegletal, derefter hvert afsnit med sine kolonner, og forbeholdene til
 * sidst. Afsnittene har hver sin bredde, og en tom raekke skiller dem - det er saadan, en
 * rapport ser ud, naar den aabnes i Excel.
 */
export function reportToCsv(report: ReportDocument): string {
  const rows: CsvValue[][] = [[report.title, report.period_label], []];

  if (report.kpis.length > 0) {
    rows.push(['Nøgletal']);
    for (const kpi of report.kpis) {
      rows.push([kpi.label, csvReportValue(kpi.type, kpi.value), kpi.hint ?? null]);
    }
    rows.push([]);
  }

  for (const section of report.sections) {
    rows.push([section.title]);
    rows.push(section.columns.map((column) => column.label));

    if (section.rows.length === 0) rows.push([section.empty]);
    for (const row of section.rows) {
      rows.push(
        section.columns.map((column) => csvReportValue(column.type, row[column.key] ?? null)),
      );
    }

    if (section.total) {
      const total = section.total;
      rows.push(
        section.columns.map((column, index) =>
          index === 0 ? 'I alt' : csvReportValue(column.type, total[column.key] ?? null),
        ),
      );
    }
    rows.push([]);
  }

  if (report.notes.length > 0) {
    rows.push(['Forbehold']);
    for (const note of report.notes) rows.push([note]);
  }

  return buildCsvLines(rows);
}

/**
 * Filnavnet: `Codepass-Maanedsrapport-2026-08.pdf`. Maaneden, naar rapporten har en, aaret paa en
 * aarsrapport - ellers datoen den er opgjort, saa to opgoerelser fra hver sin dag kan ligge i
 * samme mappe.
 */
export function reportFileName(report: ReportDocument, format: ReportExportFormat): string {
  const name = report.title
    .replaceAll('æ', 'ae')
    .replaceAll('ø', 'oe')
    .replaceAll('å', 'aa')
    .replaceAll('Æ', 'Ae')
    .replaceAll('Ø', 'Oe')
    .replaceAll('Å', 'Aa')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const period = report.month ?? (report.year ? String(report.year) : null);
  return `Codepass-${name}-${period ?? report.generated_at.slice(0, 10)}.${format}`;
}
